import React, { useState } from "react";
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Tooltip,
  Typography,
  Tabs,
  Tab,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { Configuration } from "./modules/GeneralSettings";
import { IRaffle } from "../../store/interfaces/IRaffle";
import { CreditCard, Numbers, Star } from "@mui/icons-material";
import { BankAccounts } from "./modules/BankAccounts";
import { BlockedNumbers } from "./modules/RewardedNumbers";
import { PromoEngine } from "./modules/PromoEngine";
import { BadgeCheck } from "lucide-react";
import { Remaining } from "./modules/Remaining";
import { Sellers } from "./modules/Sellers";
import { Customizations } from "./modules/Customization";
import { ChatBox } from "./modules/ChatBox";
import { BestPurchasers } from "./modules/BestPurchasers";
import { useIsMobileView } from "../../utils/responsive";

const menuItems = [
  // { label: "Diseño personalizado", key: "customization", premiumConfig: true, inDev: true, icon: <Paintbrush /> },
  // { label: "Chatbot con IA", key: "chatbox", premiumConfig: true, inDev: true, icon: <Bot /> },
  // { label: "Configuración general", key: "general", premiumConfig: false, inDev: false, icon: <Settings /> },
  { label: "Cuentas bancarias", key: "bank", premiumConfig: false, inDev: false, icon: <CreditCard /> },
  { label: "Números", key: "blocked", premiumConfig: false, inDev: false, icon: <Numbers /> },
  { label: "Mejores compradores", key: "bestPurchasers", premiumConfig: false, inDev: false, icon: <Star /> },
  // { label: "Rezagados", key: "remaining", premiumConfig: true, inDev: false, icon: <UserCheck /> },
  // { label: "Promociones", key: "promo", premiumConfig: true, inDev: false, icon: <Megaphone /> },
  // { label: "Vendedores", key: "sellers", premiumConfig: true, inDev: false, icon: <Sell /> },
];

interface ConfigurationProps {
  raffle: IRaffle;
}

export const SettingsMenu = ({ raffle }: ConfigurationProps) => {
  const [selectedMenu, setSelectedMenu] = useState("bank");
  const isMobile = useIsMobileView();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));

  const filteredMenuItems = menuItems.filter(mi => 
    (mi.key !== "blocked" || (
      raffle.accountId === 'oscar-alarcon' || 
      raffle.accountId === 'brotherifas' || 
      raffle.accountId === 'carlos-pinango' || 
      raffle.accountId === 'agudojhonny' || 
      raffle.accountId === 'a-donde-la-negra' || 
      raffle.accountId === 'minas-de-baruta' || 
      raffle.accountId === 'aurora-graterol' || 
      raffle.accountId === 'w-robles' || 
      raffle.accountId === 'rifas-chente' || 
      raffle.accountId === 'gerej-16' || 
      raffle.accountId === 'rifas-jf'
    ))
  );

  const handleMenuSelect = (key: string) => {
    setSelectedMenu(key);
  };

  const renderMenuContent = () => (
    <List sx={{ width: isMobile || isSmallScreen ? '100%' : 300, pt: isMobile || isSmallScreen ? 2 : 0 }}>
      {filteredMenuItems.map((item) => (
        <Tooltip 
          key={item.key} 
          title={item.premiumConfig ? "Funcionalidad Premium" : ""} 
          placement={isMobile || isSmallScreen ? "bottom" : "right"}
        >
          <ListItem disablePadding>
            <ListItemButton
              selected={selectedMenu === item.key}
              onClick={() => handleMenuSelect(item.key)}
              sx={{
                width: isMobile || isSmallScreen ? '100%' : 300,
                borderRadius: 1,
                my: 0.5,
                mx: isMobile || isSmallScreen ? 1 : 0,
                "&.Mui-selected": { 
                  bgcolor: "primary.main", 
                  color: "black",
                  "&:hover": { bgcolor: "primary.dark" }
                },
                "&:hover": { bgcolor: "primary.light", color: "black" },
              }}
            >
              <ListItemIcon sx={{ color: selectedMenu === item.key ? "black" : "inherit" }}>
                {item.icon}
              </ListItemIcon>
              <ListItemText sx={{ color: 'black !important' }}>
                <Typography 
                  fontWeight={"bold"} 
                  color={selectedMenu === item.key ? "black" : "gray"}
                  fontSize={isMobile || isSmallScreen ? "0.9rem" : "1rem"}
                >
                  {item.label}
                </Typography>
              </ListItemText>
              {item.premiumConfig && <BadgeCheck color="blue" size={20} />}
            </ListItemButton>
          </ListItem>
        </Tooltip>
      ))}
    </List>
  );

  const renderContent = () => (
    <>
      {selectedMenu === "general" && <Configuration raffle={raffle} />}
      {selectedMenu === "chatbox" && <ChatBox raffle={raffle} />}
      {selectedMenu === "bank" && <BankAccounts raffle={raffle} />}
      {selectedMenu === "blocked" && <BlockedNumbers raffle={raffle} />}
      {selectedMenu === "bestPurchasers" && <BestPurchasers raffle={raffle} />}
      {selectedMenu === "promo" && <PromoEngine raffle={raffle} />}
      {selectedMenu === "remaining" && <Remaining raffle={raffle} />}
      {selectedMenu === "sellers" && <Sellers raffle={raffle} />}
      {selectedMenu === "customization" && <Customizations raffle={raffle} />}
    </>
  );

  // Mobile/Tablet View
  if (isMobile || isSmallScreen) {
    const currentTabIndex = Math.max(0, filteredMenuItems.findIndex(item => item.key === selectedMenu));
    
    return (
      <Box sx={{ width: "100%" }}>
        {/* Mobile Tabs */}
        <Box sx={{ 
          borderBottom: 1, 
          borderColor: "divider",
          mb: 2,
          overflowX: "auto",
          "& .MuiTabs-scrollButtons": {
            "&.Mui-disabled": { opacity: 0.3 }
          }
        }}>
          <Tabs
            value={currentTabIndex}
            onChange={(_, newValue) => handleMenuSelect(filteredMenuItems[newValue].key)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              "& .MuiTab-root": {
                minWidth: 120,
                fontSize: "0.75rem",
                textTransform: "none",
                fontWeight: 600,
              },
              "& .Mui-selected": {
                color: "primary.main",
              },
            }}
          >
            {filteredMenuItems.map((item) => (
              <Tab
                key={item.key}
                label={item.label}
                icon={item.icon}
                iconPosition="start"
                sx={{ 
                  display: "flex",
                  flexDirection: "row",
                  gap: 1,
                  alignItems: "center",
                  justifyContent: "flex-start",
                }}
              />
            ))}
          </Tabs>
        </Box>

        {/* Mobile Content */}
        <Box sx={{ 
          width: "100%", 
          p: { xs: 1, sm: 2 },
          pb: { xs: 10, sm: 4 } // Extra padding bottom for mobile navigation
        }}>
          {renderContent()}
        </Box>
      </Box>
    );
  }

  // Desktop View
  return (
    <Box sx={{ display: "flex", width: "100%" }}>
      <Box sx={{ 
        borderRight: 1, 
        borderColor: "divider",
        minWidth: 300,
        maxWidth: 300,
      }}>
        {renderMenuContent()}
      </Box>

      {/* Main Content */}
      <Box sx={{ 
        flexGrow: 1, 
        p: { xs: 2, sm: 3, md: 4 },
        width: "100%",
        overflow: "auto"
      }}>
        {renderContent()}
      </Box>
    </Box>
  );
};

export default SettingsMenu;
